import type { ProductCapabilityKey } from './capabilities';

export type CapabilityGroupCategory =
  | 'guest_experience'
  | 'operations'
  | 'automation'
  | 'integrations'
  | 'publishing'
  | 'advanced_controls';

export type CapabilityPackageAudience =
  | 'planner'
  | 'vendor'
  | 'mixed'
  | 'internal';

export type CapabilityPackageComplexity = 'simple' | 'advanced';

export interface CapabilityGroupDefinition {
  key: string;
  displayName: string;
  description: string;
  category: CapabilityGroupCategory;
  capabilityKeys: ProductCapabilityKey[];
  sortOrder: number;
  metadata?: {
    visibility?: 'planner_admin' | 'internal';
    docsLabel?: string;
  };
}

export interface CapabilityPackageDefinition {
  key: string;
  displayName: string;
  description: string;
  includedGroupKeys: string[];
  includedCapabilityKeys: ProductCapabilityKey[];
  removedCapabilityKeys: ProductCapabilityKey[];
  metadata: {
    recommendedAudience: CapabilityPackageAudience;
    complexityLevel: CapabilityPackageComplexity;
    onboardingPreset: boolean;
    billingCompatible: boolean;
    suggestedUseCases: string[];
  };
}

export interface CapabilityPackagingValidationIssue {
  code:
    | 'duplicate_group_key'
    | 'duplicate_package_key'
    | 'unknown_capability'
    | 'duplicate_group_capability'
    | 'duplicate_package_group'
    | 'duplicate_package_capability'
    | 'duplicate_package_removed_capability'
    | 'package_override_conflict'
    | 'unknown_group'
    | 'incomplete_package_definition';
  message: string;
}

export interface CapabilityPackageResolutionStep {
  action:
    | 'package_selected'
    | 'group_expanded'
    | 'capability_included'
    | 'capability_skipped_duplicate'
    | 'capability_removed';
  packageKey: string | null;
  groupKey: string | null;
  capabilityKey: ProductCapabilityKey | null;
  reason: string;
}

export interface CapabilityPackageResolution {
  packageKeys: string[];
  packages: CapabilityPackageDefinition[];
  groups: CapabilityGroupDefinition[];
  seedCapabilityKeys: ProductCapabilityKey[];
  removedCapabilityKeys: ProductCapabilityKey[];
  finalCapabilityKeys: ProductCapabilityKey[];
  explanation: CapabilityPackageResolutionStep[];
  capabilityOrigins: Partial<
    Record<
      ProductCapabilityKey,
      {
        packageKey: string | null;
        groupKey: string | null;
      }
    >
  >;
}

export interface CapabilityPackagingMetadata {
  assignedPackageKeys: string[];
  suggestedPackageKeys: string[];
}

export const capabilityGroupDefinitions = [
  {
    key: 'guest_experience',
    displayName: 'Guest Experience',
    description:
      'Capabilities that shape how guests encounter localized and polished occasion experiences.',
    category: 'guest_experience',
    capabilityKeys: ['multi_language'],
    sortOrder: 10,
    metadata: {
      visibility: 'planner_admin',
      docsLabel: 'Guest-facing experience',
    },
  },
  {
    key: 'publishing_and_distribution',
    displayName: 'Publishing and Distribution',
    description:
      'Capabilities that enable publishing, export-ready, and distribution-oriented publishing outputs.',
    category: 'publishing',
    capabilityKeys: ['publishing'],
    sortOrder: 20,
    metadata: {
      visibility: 'planner_admin',
      docsLabel: 'Publishing',
    },
  },
  {
    key: 'vendor_tools',
    displayName: 'Vendor Tools',
    description:
      'Capabilities that enable vendor-scoped participation and coordination surfaces.',
    category: 'operations',
    capabilityKeys: ['vendor_access'],
    sortOrder: 30,
    metadata: {
      visibility: 'planner_admin',
      docsLabel: 'Vendor operations',
    },
  },
  {
    key: 'integrations',
    displayName: 'Integrations',
    description:
      'Capabilities that enable occasion-scoped API connectivity and outbound webhook operations.',
    category: 'integrations',
    capabilityKeys: ['api_access', 'webhooks'],
    sortOrder: 40,
    metadata: {
      visibility: 'planner_admin',
      docsLabel: 'External connectivity',
    },
  },
  {
    key: 'automation',
    displayName: 'Automation',
    description:
      'Capabilities that support event-driven automations and system-to-system orchestration.',
    category: 'automation',
    capabilityKeys: ['api_access', 'webhooks'],
    sortOrder: 50,
    metadata: {
      visibility: 'planner_admin',
      docsLabel: 'Automation',
    },
  },
  {
    key: 'advanced_controls',
    displayName: 'Advanced Controls',
    description:
      'A broader operator-facing cluster used for explaining complex, premium planner setups.',
    category: 'advanced_controls',
    capabilityKeys: [
      'multi_language',
      'publishing',
      'api_access',
      'webhooks',
      'vendor_access',
    ],
    sortOrder: 60,
    metadata: {
      visibility: 'planner_admin',
      docsLabel: 'Advanced planning',
    },
  },
] satisfies CapabilityGroupDefinition[];

export const capabilityPackageDefinitions = [
  {
    key: 'simple_starter_experience',
    displayName: 'Simple Starter Experience',
    description:
      'A guided baseline preset that keeps premium capability seeds empty until the occasion deliberately upgrades.',
    includedGroupKeys: [],
    includedCapabilityKeys: [],
    removedCapabilityKeys: [],
    metadata: {
      recommendedAudience: 'planner',
      complexityLevel: 'simple',
      onboardingPreset: true,
      billingCompatible: true,
      suggestedUseCases: [
        'single-occasion launch',
        'guided onboarding',
        'baseline starter packaging',
      ],
    },
  },
  {
    key: 'pro_planning_toolkit',
    displayName: 'Pro Planning Toolkit',
    description:
      'A full premium planner bundle for multilingual guest flows, publishing outputs, integrations, and vendor coordination.',
    includedGroupKeys: [
      'guest_experience',
      'publishing_and_distribution',
      'integrations',
      'vendor_tools',
    ],
    includedCapabilityKeys: [],
    removedCapabilityKeys: [],
    metadata: {
      recommendedAudience: 'planner',
      complexityLevel: 'advanced',
      onboardingPreset: true,
      billingCompatible: true,
      suggestedUseCases: [
        'planner pro onboarding',
        'default premium tier bundle',
        'full-service occasion operations',
      ],
    },
  },
  {
    key: 'vendor_heavy_operational_setup',
    displayName: 'Vendor-heavy Operational Setup',
    description:
      'A preset tuned for occasions that rely heavily on external vendors and coordination workflows.',
    includedGroupKeys: ['vendor_tools', 'integrations'],
    includedCapabilityKeys: [],
    removedCapabilityKeys: [],
    metadata: {
      recommendedAudience: 'mixed',
      complexityLevel: 'advanced',
      onboardingPreset: true,
      billingCompatible: true,
      suggestedUseCases: [
        'venue-heavy operations',
        'vendor portal rollouts',
        'high-coordination productions',
      ],
    },
  },
  {
    key: 'advanced_automation_bundle',
    displayName: 'Advanced Automation Bundle',
    description:
      'A focused preset for event-driven syncs, webhook consumers, and automation-heavy operators.',
    includedGroupKeys: ['automation'],
    includedCapabilityKeys: [],
    removedCapabilityKeys: [],
    metadata: {
      recommendedAudience: 'planner',
      complexityLevel: 'advanced',
      onboardingPreset: true,
      billingCompatible: true,
      suggestedUseCases: [
        'automation-first rollout',
        'integration-heavy operations',
        'beta/internal operator presets',
      ],
    },
  },
  {
    key: 'api_integration_bundle',
    displayName: 'API Integration Bundle',
    description:
      'A narrower package for API and webhook enablement without implying the rest of the premium planner stack.',
    includedGroupKeys: ['integrations'],
    includedCapabilityKeys: [],
    removedCapabilityKeys: [],
    metadata: {
      recommendedAudience: 'planner',
      complexityLevel: 'advanced',
      onboardingPreset: true,
      billingCompatible: true,
      suggestedUseCases: [
        'partner integrations',
        'platform-to-platform automation',
        'technical onboarding',
      ],
    },
  },
  {
    key: 'print_heavy_distribution',
    displayName: 'Print-heavy Distribution',
    description:
      'A preset for operators who primarily need publishing artifact generation and distribution workflows.',
    includedGroupKeys: ['publishing_and_distribution'],
    includedCapabilityKeys: [],
    removedCapabilityKeys: [],
    metadata: {
      recommendedAudience: 'planner',
      complexityLevel: 'advanced',
      onboardingPreset: true,
      billingCompatible: true,
      suggestedUseCases: [
        'print-centric occasions',
        'invitation and signage production',
        'publishing-first rollouts',
      ],
    },
  },
  {
    key: 'studio_signature_stack',
    displayName: 'Studio Signature Stack',
    description:
      'A broad internal-facing premium composition that layers the full pro toolkit with additional explainable premium posture.',
    includedGroupKeys: ['advanced_controls'],
    includedCapabilityKeys: [],
    removedCapabilityKeys: [],
    metadata: {
      recommendedAudience: 'mixed',
      complexityLevel: 'advanced',
      onboardingPreset: false,
      billingCompatible: true,
      suggestedUseCases: [
        'high-touch studio operations',
        'full premium launch profile',
        'internal packaging reference',
      ],
    },
  },
] satisfies CapabilityPackageDefinition[];

function unique<T>(values: T[]) {
  return [...new Set(values)];
}

function createCapabilitySet(validCapabilityKeys: ProductCapabilityKey[]) {
  return new Set(validCapabilityKeys);
}

function createGroupMap(groups: CapabilityGroupDefinition[]) {
  return new Map(groups.map((group) => [group.key, group]));
}

function createPackageMap(packages: CapabilityPackageDefinition[]) {
  return new Map(packages.map((entry) => [entry.key, entry]));
}

export function validateCapabilityGroupDefinitions(
  groups: CapabilityGroupDefinition[],
  validCapabilityKeys: ProductCapabilityKey[],
) {
  const issues: CapabilityPackagingValidationIssue[] = [];
  const groupKeys = new Set<string>();
  const capabilitySet = createCapabilitySet(validCapabilityKeys);

  for (const group of groups) {
    if (groupKeys.has(group.key)) {
      issues.push({
        code: 'duplicate_group_key',
        message: `Capability group "${group.key}" is defined more than once.`,
      });
      continue;
    }

    groupKeys.add(group.key);

    const seenCapabilityKeys = new Set<ProductCapabilityKey>();

    for (const capabilityKey of group.capabilityKeys) {
      if (!capabilitySet.has(capabilityKey)) {
        issues.push({
          code: 'unknown_capability',
          message: `Capability group "${group.key}" references unknown capability "${capabilityKey}".`,
        });
      }

      if (seenCapabilityKeys.has(capabilityKey)) {
        issues.push({
          code: 'duplicate_group_capability',
          message: `Capability group "${group.key}" lists capability "${capabilityKey}" more than once.`,
        });
      }

      seenCapabilityKeys.add(capabilityKey);
    }
  }

  return issues;
}

export function validateCapabilityPackageDefinitions(
  packages: CapabilityPackageDefinition[],
  groups: CapabilityGroupDefinition[],
  validCapabilityKeys: ProductCapabilityKey[],
) {
  const issues: CapabilityPackagingValidationIssue[] = [];
  const packageKeys = new Set<string>();
  const groupMap = createGroupMap(groups);
  const capabilitySet = createCapabilitySet(validCapabilityKeys);

  for (const entry of packages) {
    if (packageKeys.has(entry.key)) {
      issues.push({
        code: 'duplicate_package_key',
        message: `Capability package "${entry.key}" is defined more than once.`,
      });
      continue;
    }

    packageKeys.add(entry.key);

    if (
      entry.includedGroupKeys.length === 0 &&
      entry.includedCapabilityKeys.length === 0 &&
      entry.removedCapabilityKeys.length === 0
    ) {
      if (entry.key !== 'simple_starter_experience') {
        issues.push({
          code: 'incomplete_package_definition',
          message: `Capability package "${entry.key}" does not include groups, direct capabilities, or explicit removals.`,
        });
      }
    }

    const seenGroupKeys = new Set<string>();
    const seenIncludedCapabilityKeys = new Set<ProductCapabilityKey>();
    const seenRemovedCapabilityKeys = new Set<ProductCapabilityKey>();

    for (const groupKey of entry.includedGroupKeys) {
      if (!groupMap.has(groupKey)) {
        issues.push({
          code: 'unknown_group',
          message: `Capability package "${entry.key}" references unknown group "${groupKey}".`,
        });
      }

      if (seenGroupKeys.has(groupKey)) {
        issues.push({
          code: 'duplicate_package_group',
          message: `Capability package "${entry.key}" lists group "${groupKey}" more than once.`,
        });
      }

      seenGroupKeys.add(groupKey);
    }

    for (const capabilityKey of entry.includedCapabilityKeys) {
      if (!capabilitySet.has(capabilityKey)) {
        issues.push({
          code: 'unknown_capability',
          message: `Capability package "${entry.key}" directly includes unknown capability "${capabilityKey}".`,
        });
      }

      if (seenIncludedCapabilityKeys.has(capabilityKey)) {
        issues.push({
          code: 'duplicate_package_capability',
          message: `Capability package "${entry.key}" directly includes capability "${capabilityKey}" more than once.`,
        });
      }

      seenIncludedCapabilityKeys.add(capabilityKey);
    }

    for (const capabilityKey of entry.removedCapabilityKeys) {
      if (!capabilitySet.has(capabilityKey)) {
        issues.push({
          code: 'unknown_capability',
          message: `Capability package "${entry.key}" removes unknown capability "${capabilityKey}".`,
        });
      }

      if (seenRemovedCapabilityKeys.has(capabilityKey)) {
        issues.push({
          code: 'duplicate_package_removed_capability',
          message: `Capability package "${entry.key}" removes capability "${capabilityKey}" more than once.`,
        });
      }

      if (seenIncludedCapabilityKeys.has(capabilityKey)) {
        issues.push({
          code: 'package_override_conflict',
          message: `Capability package "${entry.key}" both includes and removes capability "${capabilityKey}".`,
        });
      }

      seenRemovedCapabilityKeys.add(capabilityKey);
    }
  }

  return issues;
}

export function assertCapabilityPackagingDefinitions(
  groups: CapabilityGroupDefinition[],
  packages: CapabilityPackageDefinition[],
  validCapabilityKeys: ProductCapabilityKey[],
) {
  const issues = [
    ...validateCapabilityGroupDefinitions(groups, validCapabilityKeys),
    ...validateCapabilityPackageDefinitions(
      packages,
      groups,
      validCapabilityKeys,
    ),
  ];

  if (issues.length > 0) {
    throw new Error(
      `Capability packaging definitions are invalid: ${issues
        .map((issue) => issue.message)
        .join(' ')}`,
    );
  }
}

export function listCapabilityGroups() {
  return capabilityGroupDefinitions
    .map((group) => ({
      ...group,
      capabilityKeys: [...group.capabilityKeys],
      metadata: group.metadata ? { ...group.metadata } : undefined,
    }))
    .sort((left, right) => left.sortOrder - right.sortOrder);
}

export function listCapabilityPackages() {
  return capabilityPackageDefinitions.map((entry) => ({
    ...entry,
    includedGroupKeys: [...entry.includedGroupKeys],
    includedCapabilityKeys: [...entry.includedCapabilityKeys],
    removedCapabilityKeys: [...entry.removedCapabilityKeys],
    metadata: {
      ...entry.metadata,
      suggestedUseCases: [...entry.metadata.suggestedUseCases],
    },
  }));
}

export function getCapabilityGroupDefinition(groupKey: string) {
  return createGroupMap(capabilityGroupDefinitions).get(groupKey) ?? null;
}

export function getCapabilityPackageDefinition(packageKey: string) {
  return createPackageMap(capabilityPackageDefinitions).get(packageKey) ?? null;
}

export function listOnboardingCapabilityPackages() {
  return listCapabilityPackages().filter(
    (entry) => entry.metadata.onboardingPreset,
  );
}

export function parseCapabilityPackagingMetadata(
  metadata: Record<string, unknown> | null | undefined,
): CapabilityPackagingMetadata {
  const assignedValue = metadata?.capability_package_keys;
  const suggestedValue = metadata?.suggested_capability_package_keys;

  function parseField(value: unknown, fieldName: string) {
    if (value === undefined || value === null) {
      return [];
    }

    if (!Array.isArray(value)) {
      throw new Error(
        `Capability packaging metadata field "${fieldName}" must be an array of package keys.`,
      );
    }

    const packageKeys = value.map((entry) => {
      if (typeof entry !== 'string') {
        throw new Error(
          `Capability packaging metadata field "${fieldName}" contains a non-string package key.`,
        );
      }

      if (!getCapabilityPackageDefinition(entry)) {
        throw new Error(
          `Capability packaging metadata field "${fieldName}" references unknown package "${entry}".`,
        );
      }

      return entry;
    });

    return unique(packageKeys);
  }

  return {
    assignedPackageKeys: parseField(assignedValue, 'capability_package_keys'),
    suggestedPackageKeys: parseField(
      suggestedValue,
      'suggested_capability_package_keys',
    ),
  };
}

export function resolveCapabilityPackageKeys(input: { packageKeys: string[] }) {
  const requestedPackageKeys = unique(input.packageKeys);
  const packageMap = createPackageMap(capabilityPackageDefinitions);
  const groupMap = createGroupMap(capabilityGroupDefinitions);
  const packages: CapabilityPackageDefinition[] = [];
  const groups = new Map<string, CapabilityGroupDefinition>();
  const seedCapabilityKeys: ProductCapabilityKey[] = [];
  const removedCapabilityKeys: ProductCapabilityKey[] = [];
  const explanation: CapabilityPackageResolutionStep[] = [];
  const capabilityOrigins: CapabilityPackageResolution['capabilityOrigins'] =
    {};

  for (const packageKey of requestedPackageKeys) {
    const entry = packageMap.get(packageKey);

    if (!entry) {
      throw new Error(
        `Cannot resolve capability package "${packageKey}" because it is not defined.`,
      );
    }

    packages.push(entry);
    explanation.push({
      action: 'package_selected',
      packageKey,
      groupKey: null,
      capabilityKey: null,
      reason: `Package "${packageKey}" was selected for resolution.`,
    });

    for (const groupKey of entry.includedGroupKeys) {
      const group = groupMap.get(groupKey);

      if (!group) {
        throw new Error(
          `Capability package "${packageKey}" references unknown group "${groupKey}".`,
        );
      }

      groups.set(groupKey, group);
      explanation.push({
        action: 'group_expanded',
        packageKey,
        groupKey,
        capabilityKey: null,
        reason: `Group "${groupKey}" was expanded from package "${packageKey}".`,
      });

      for (const capabilityKey of group.capabilityKeys) {
        if (seedCapabilityKeys.includes(capabilityKey)) {
          explanation.push({
            action: 'capability_skipped_duplicate',
            packageKey,
            groupKey,
            capabilityKey,
            reason: `Capability "${capabilityKey}" was already seeded earlier and stayed deduplicated.`,
          });
          continue;
        }

        seedCapabilityKeys.push(capabilityKey);
        capabilityOrigins[capabilityKey] = {
          packageKey,
          groupKey,
        };
        explanation.push({
          action: 'capability_included',
          packageKey,
          groupKey,
          capabilityKey,
          reason: `Capability "${capabilityKey}" was seeded from group "${groupKey}".`,
        });
      }
    }

    for (const capabilityKey of entry.includedCapabilityKeys) {
      if (seedCapabilityKeys.includes(capabilityKey)) {
        explanation.push({
          action: 'capability_skipped_duplicate',
          packageKey,
          groupKey: null,
          capabilityKey,
          reason: `Capability "${capabilityKey}" was already seeded before direct package inclusion.`,
        });
        continue;
      }

      seedCapabilityKeys.push(capabilityKey);
      capabilityOrigins[capabilityKey] = {
        packageKey,
        groupKey: null,
      };
      explanation.push({
        action: 'capability_included',
        packageKey,
        groupKey: null,
        capabilityKey,
        reason: `Capability "${capabilityKey}" was directly included by package "${packageKey}".`,
      });
    }

    for (const capabilityKey of entry.removedCapabilityKeys) {
      if (!removedCapabilityKeys.includes(capabilityKey)) {
        removedCapabilityKeys.push(capabilityKey);
      }

      explanation.push({
        action: 'capability_removed',
        packageKey,
        groupKey: null,
        capabilityKey,
        reason: `Capability "${capabilityKey}" was explicitly removed by package "${packageKey}".`,
      });
    }
  }

  const finalCapabilityKeys = seedCapabilityKeys.filter(
    (capabilityKey) => !removedCapabilityKeys.includes(capabilityKey),
  );

  return {
    packageKeys: requestedPackageKeys,
    packages,
    groups: [...groups.values()].sort(
      (left, right) => left.sortOrder - right.sortOrder,
    ),
    seedCapabilityKeys,
    removedCapabilityKeys,
    finalCapabilityKeys,
    explanation,
    capabilityOrigins,
  } satisfies CapabilityPackageResolution;
}
